import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const imageUrlPattern = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

const buildPlacementLabel = (file = {}) => {
  const code = String(file?.placementCode || "").trim();
  const name = String(file?.placementName || "").trim();
  if (code && name && code !== name) return `${code} — ${name}`;
  return code || name || "Unassigned";
};

const inferSourceLabel = (file = {}) => {
  if (file?.source === "binated_zip") return "Binated file";
  if (file?.source === "provider") return "Binated file";
  return "Submitted artwork";
};

const isImageFile = (file = {}) => {
  if (file?.isImage === true) return true;
  const url = String(file?.previewUrl || file?.downloadUrl || file?.fileName || "");
  return imageUrlPattern.test(url);
};

export default function BinatedLifecycleFilesTable({
  files = [],
  statusItems = [],
  revisionAllowed = true,
  onRequestRevision,
  disabled = false,
}) {
  const hasStatusItems = Array.isArray(statusItems) && statusItems.length > 0;
  const canUseFileRevision = typeof onRequestRevision === "function";

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Files & Artwork
      </Typography>

      {files.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          {files.map((file, idx) => {
            const matchedStatus = String(
              file?.normalizedStatus || file?.matchedStatusItem?.normalizedStatus || ""
            ).toLowerCase();
            const canRequestRevision =
              revisionAllowed &&
              !disabled &&
              matchedStatus !== "action_required";

            return (
              <Paper
                key={`${file?.downloadUrl || file?.previewUrl || file?.fileName || "file"}-${idx}`}
                elevation={0}
                sx={{
                  p: 1.25,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 92,
                      height: 92,
                      borderRadius: 1.5,
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                      overflow: "hidden",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    {isImageFile(file) && (file?.previewUrl || file?.downloadUrl) ? (
                      <Box
                        component="img"
                        src={file?.previewUrl || file?.downloadUrl}
                        alt={file?.fileName || "Binated artwork"}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", px: 1 }}>
                        File Preview
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                      {file?.fileName || "Artwork file"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {buildPlacementLabel(file)}
                    </Typography>
                    {(file?.productSku || file?.productName) && (
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        {[file?.productSku, file?.productName].filter(Boolean).join(" — ")}
                      </Typography>
                    )}
                    {file?.garment ? (
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        {file.garment}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.75 }}>
                      <Chip size="small" label={inferSourceLabel(file)} variant="outlined" />
                      {file?.matchedByPlacement || file?.productSku ? (
                        <Chip size="small" color="success" label="Placement matched" />
                      ) : hasStatusItems ? (
                        <Chip size="small" label="Unassigned" />
                      ) : null}
                      {matchedStatus ? (
                        <Chip size="small" color={matchedStatus === "action_required" ? "warning" : "default"} label={matchedStatus.replace(/_/g, " ")} />
                      ) : null}
                    </Stack>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="text"
                    href={file?.previewUrl || file?.downloadUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!file?.previewUrl && !file?.downloadUrl}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    href={file?.downloadUrl || file?.previewUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!file?.downloadUrl && !file?.previewUrl}
                  >
                    Download
                  </Button>
                  {canUseFileRevision ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onRequestRevision?.(file)}
                      disabled={!canRequestRevision}
                    >
                      Request Revision
                    </Button>
                  ) : null}
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{ p: 1.5, border: "1px dashed #cbd5e1", borderRadius: 2, background: "#fff" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No Binated artwork files are available yet. Refresh this request after Binated returns artwork assets.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
